import { Fragment, useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CAMPO_BASE, Recado } from "./ui";
import { IconeEditar } from "./icones";
import { numero } from "../lib/admin";
import AvaliacoesDaFerramenta from "./AdminAvaliacoesFerramenta";
import { AvisoDaLeitura, LinhaDoPeriodo, SeloDaSituacao, fraseSolta } from "./AdminAvaliacoesUi";
import {
  buscaSoDoPeriodo,
  chaveDoPedido,
  chaveDoPeriodo,
  definirMeta,
  lerComparacao,
  metaDoTexto,
  metaLegivel,
  notaLegivel,
  pctLegivel,
  quandoLegivel,
  useLeitura,
  type Comparacao,
  type LinhaDaFerramenta,
  type PedidoDePeriodo,
  type RespostaDaMeta,
  type SituacaoDaMeta,
} from "../lib/avaliacoes";

/**
 * Admin › Avaliações › Ferramentas (000327/000328): todas as ferramentas lado a
 * lado, com a média do período, a meta de cada uma e a situação — e a página
 * de UMA (`?card=<slug>`), com a meta, os números, a evolução e os comentários.
 *
 * A META se muda nos DOIS lugares, como no painel antigo ("Meta 9,0 ✎" na
 * lista e no detalhe): na lista, a linha de edição abre logo abaixo da
 * ferramenta, para o time ajustar as metas de várias sem abrir uma página por
 * vez; na página, o bloco da meta mostra a média e a situação ao lado. Os dois
 * chamam a MESMA função do banco (`av_admin_definir_meta`), e a frase da
 * resposta é a dela — é ela que diz o que aconteceu, inclusive a recusa.
 *
 * ZERO conta aqui: a média, as "satisfeitas" e as "baixas", a equipe fora da
 * conta e a situação da meta vêm prontas do banco (`av_admin_comparacao`). A
 * tela ordena e formata.
 */
export default function AdminAvaliacoesFerramentas({
  pedido,
  podeEditar,
  veCatalogo,
}: {
  pedido: PedidoDePeriodo;
  podeEditar: boolean;
  veCatalogo: boolean;
}) {
  const [busca] = useSearchParams();
  const card = busca.get("card");
  const { estado, recarregar } = useLeitura((s) => lerComparacao(pedido, s), chaveDoPedido(pedido));

  if (estado.fase !== "pronto") return <AvisoDaLeitura estado={estado} />;
  const { dados, atualizando } = estado;
  const periodo = buscaSoDoPeriodo(pedido);

  return (
    <div
      className={`transition-opacity ${atualizando ? "opacity-60" : ""}`}
      aria-busy={atualizando}
      data-aba-das-avaliacoes="ferramentas"
      data-painel-pronto={!atualizando}
      data-periodo-dos-dados={chaveDoPeriodo(dados.periodo)}
    >
      <LinhaDoPeriodo periodo={dados.periodo} atualizando={atualizando} />
      <div className="mt-4">
        {card ? (
          <PaginaDaFerramenta
            key={card}
            dados={dados}
            slug={card}
            pedido={pedido}
            periodo={periodo}
            podeEditar={podeEditar}
            veCatalogo={veCatalogo}
            aoMudar={recarregar}
          />
        ) : (
          <ListaDasFerramentas dados={dados} periodo={periodo} podeEditar={podeEditar} aoMudar={recarregar} />
        )}
      </div>
    </div>
  );
}

// ── O que as duas edições da meta compartilham ─────────────────────────────

type RecadoDaMeta = { tipo: "ok" | "erro"; texto: string };

/**
 * A frase do banco, pronta para ficar solta na tela. O banco a escreve em
 * minúscula ("a meta de X agora é 8,5."), como continuação de quem chama — a
 * mesma decisão de `fraseDoAviso`, nas turmas da pós. A inicial maiúscula e o
 * ponto são os de `fraseSolta`, a peça que as quatro sub-abas usam.
 */
function fraseDaResposta(r: RespostaDaMeta): string {
  return fraseSolta(r.detalhe ?? (r.ok ? "feito." : "não deu certo — tente de novo."));
}

/**
 * O campo que não é número nem chega ao banco. Vazio, com meta gravada, a
 * frase aponta o caminho de tirar: esvaziar o campo e salvar não tira a meta —
 * tirar é um ato próprio, e o histórico o registra como tal.
 */
function fraseDoCampoInvalido(temMeta: boolean, vazio: boolean): string {
  const base = "Escreva a meta como número, de 0 a 10 — por exemplo, 8,5.";
  return vazio && temMeta ? `${base} Para ficar sem meta, use «Tirar a meta».` : base;
}

/**
 * O foco que caiu no corpo da página volta ao campo da meta. O botão apertado
 * fica desligado enquanto a meta vai ao banco, e o navegador tira o foco de um
 * elemento desligado (a regra de "focus fixup" do HTML): sem isto, quem salva
 * pelo teclado perde o lugar, e o Esc da linha de edição deixa de responder.
 */
function devolverFocoSePerdido(campo: HTMLInputElement | null) {
  const ativo = document.activeElement;
  if (!ativo || ativo === document.body) campo?.focus();
}

// ── A lista ─────────────────────────────────────────────────────────────────

type Ordem = "nome" | "media" | "situacao" | "avaliacoes";

/** "Abaixo" primeiro: ordenar pela situação é procurar o que precisa de alguém. */
const PESO_DA_SITUACAO: Record<SituacaoDaMeta, number> = { abaixo: 0, atingida: 1, sem_avaliacao: 2, sem_meta: 3 };

/** As colunas da tabela: a linha de edição da meta ocupa todas (`colSpan`). */
const COLUNAS_DA_LISTA = 8;

function nomesEmFrase(nomes: string[]): string {
  if (nomes.length <= 1) return nomes[0] ?? "";
  return `${nomes.slice(0, -1).join(", ")} e ${nomes[nomes.length - 1]}`;
}

function Numero({ rotulo, valor, nota, atencao = false, dado }: { rotulo: string; valor: string; nota: ReactNode; atencao?: boolean; dado: string }) {
  return (
    <div className={`surface p-4 ${atencao ? "ring-1 ring-laranja/45" : ""}`}>
      <dt className="text-xs font-semibold tracking-wide text-cinza uppercase">{rotulo}</dt>
      <dd
        className={`mt-1 font-display text-3xl font-bold ${atencao ? "text-alerta-tinta" : "text-titulo"}`}
        data-numero-do-painel={dado}
      >
        {valor}
      </dd>
      <dd className="mt-1 text-xs leading-snug text-cinza">{nota}</dd>
    </div>
  );
}

function ListaDasFerramentas({
  dados,
  periodo,
  podeEditar,
  aoMudar,
}: {
  dados: Comparacao;
  periodo: string;
  podeEditar: boolean;
  aoMudar: () => void;
}) {
  const idTitulo = useId();
  const [ordem, setOrdem] = useState<{ por: Ordem; desc: boolean }>({ por: "nome", desc: false });
  const r = dados.resumo;
  const semAvaliacaoComMeta = r.com_meta - r.atingiram - r.abaixo_da_meta;
  const abaixo = dados.ferramentas.filter((f) => f.situacao_meta === "abaixo").map((f) => f.nome);

  // UMA linha de edição por vez: duas abertas dariam duas frases do banco na
  // tela, e a pessoa não saberia de qual ferramenta é cada uma.
  const [editando, setEditando] = useState<string | null>(null);
  // O foco volta ao botão que abriu a edição quando ela fecha. O botão está
  // sempre na tela (a linha dele não some), então ele é achado pelo slug.
  const botoes = useRef(new Map<string, HTMLButtonElement>());
  const devolverFocoA = useRef<string | null>(null);
  useEffect(() => {
    if (editando !== null) return;
    const slug = devolverFocoA.current;
    devolverFocoA.current = null;
    if (slug) botoes.current.get(slug)?.focus();
  }, [editando]);

  function alternarEditor(slug: string) {
    setEditando((atual) => (atual === slug ? null : slug));
  }
  function fecharEditor() {
    devolverFocoA.current = editando;
    setEditando(null);
  }
  const idDoEditor = (slug: string) => `${idTitulo}-meta-${slug}`;

  const linhas = useMemo(() => {
    const l = [...dados.ferramentas];
    const sinal = ordem.desc ? -1 : 1;
    const nulosNoFim = (a: number | null, b: number | null) => (a === null ? 1 : 0) - (b === null ? 1 : 0);
    l.sort((a, b) => {
      if (ordem.por === "media") {
        return nulosNoFim(a.media_geral, b.media_geral) || sinal * ((a.media_geral ?? 0) - (b.media_geral ?? 0)) || a.nome.localeCompare(b.nome, "pt-BR");
      }
      if (ordem.por === "avaliacoes") return sinal * (a.avaliacoes - b.avaliacoes) || a.nome.localeCompare(b.nome, "pt-BR");
      if (ordem.por === "situacao") {
        return sinal * (PESO_DA_SITUACAO[a.situacao_meta] - PESO_DA_SITUACAO[b.situacao_meta]) || a.nome.localeCompare(b.nome, "pt-BR");
      }
      return sinal * a.nome.localeCompare(b.nome, "pt-BR") || a.slug.localeCompare(b.slug);
    });
    return l;
  }, [dados.ferramentas, ordem]);

  function ordenarPor(por: Ordem) {
    // A primeira vez numa coluna de número desce (a maior em cima); nome e situação sobem.
    setOrdem((o) => (o.por === por ? { por, desc: !o.desc } : { por, desc: por === "media" || por === "avaliacoes" }));
  }
  const ariaSort = (por: Ordem) => (ordem.por === por ? (ordem.desc ? "descending" : "ascending") : "none");
  // Uma FUNÇÃO que devolve o cabeçalho, e não um componente declarado aqui
  // dentro: componente novo a cada render remonta o botão, e quem ordena pelo
  // teclado perde o foco no primeiro clique.
  const cabecalho = (por: Ordem, rotulo: string, direita = false) => (
    <th scope="col" aria-sort={ariaSort(por)} className={`py-2 pr-4 font-semibold ${direita ? "text-right" : "text-left"}`}>
      <button
        type="button"
        onClick={() => ordenarPor(por)}
        className="inline-flex items-center gap-1 rounded hover:text-tinta"
        data-ordenar-por={por}
      >
        {rotulo}
        <span aria-hidden="true" className="text-[0.65rem]">
          {ordem.por === por ? (ordem.desc ? "▼" : "▲") : ""}
        </span>
      </button>
    </th>
  );

  return (
    <>
      <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-numeros-do-painel>
        <Numero
          rotulo="Média geral"
          dado="media"
          valor={notaLegivel(r.media_geral)}
          nota={
            r.avaliacoes > 0
              ? `de 10, nas ${numero(r.avaliacoes)} avaliações de alunos`
              : dados.periodo.comparacao === "sem_dado_ainda"
                ? "o hub ainda não tem avaliação deste período"
                : "nenhuma avaliação de aluno no período"
          }
        />
        <Numero
          rotulo="Avaliações de alunos"
          dado="avaliacoes"
          valor={numero(r.avaliacoes)}
          nota={`em ${numero(r.com_avaliacao)} das ${numero(r.ferramentas)} ferramentas da lista`}
        />
        <Numero
          rotulo="Atingiram a meta"
          dado="atingiram"
          valor={numero(r.atingiram)}
          nota={
            r.com_meta === 0
              ? "nenhuma ferramenta tem meta ainda"
              : `de ${numero(r.com_meta)} com meta${semAvaliacaoComMeta > 0 ? ` — ${numero(semAvaliacaoComMeta)} sem avaliação no período` : ""}`
          }
        />
        <Numero
          rotulo="Abaixo da meta"
          dado="abaixo"
          valor={numero(r.abaixo_da_meta)}
          atencao={r.abaixo_da_meta > 0}
          nota={
            abaixo.length === 0
              ? r.com_meta === 0
                ? "sem meta, não há o que comparar"
                : "nenhuma"
              : abaixo.length <= 3
                ? nomesEmFrase(abaixo)
                : "ordene a lista pela situação para vê-las"
          }
        />
      </dl>

      <section className="surface mt-5 p-5 sm:p-6" aria-labelledby={idTitulo} data-tabela-das-ferramentas>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-2xl">
            <h2 id={idTitulo} className="font-display text-lg font-bold text-titulo">
              As ferramentas no período
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-cinza">
              Clique no nome de uma para ver a evolução e os comentários dela.
              {podeEditar
                ? " Na coluna Meta, o botão abre a edição ali mesmo, logo abaixo da linha."
                : " A meta muda quem tem edição na aba Avaliações."}
            </p>
          </div>
          <Link
            to={`/admin/avaliacoes/comparar${periodo ? `?${periodo}` : ""}`}
            className="btn-contorno min-h-10 shrink-0 text-sm"
            data-ir-comparar
          >
            Comparar ferramentas
          </Link>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[46rem] text-sm" data-lista-das-ferramentas>
            <caption className="sr-only">As ferramentas, com a média, a meta, a situação e o volume de avaliações do período</caption>
            <thead>
              <tr className="border-b border-borda text-xs text-cinza">
                {cabecalho("nome", "Ferramenta")}
                {cabecalho("media", "Média", true)}
                <th scope="col" className="py-2 pr-4 text-right font-semibold">
                  Meta
                </th>
                {cabecalho("situacao", "Situação")}
                {cabecalho("avaliacoes", "Avaliações", true)}
                <th scope="col" className="py-2 pr-4 text-right font-semibold">
                  Satisfeitas
                </th>
                <th scope="col" className="py-2 pr-4 text-right font-semibold">
                  Baixas
                </th>
                <th scope="col" className="py-2 text-right font-semibold">
                  Última
                </th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((l) => {
                const aberto = editando === l.slug;
                return (
                  <Fragment key={l.slug}>
                    <tr
                      // Aberta, a linha e a edição dela são uma peça só: o mesmo
                      // fundo, e nenhum fio entre as duas.
                      className={aberto ? "bg-superficie-2" : "border-b border-borda/60 last:border-0"}
                      data-ferramenta={l.slug}
                      data-situacao={l.situacao_meta}
                    >
                      <td className="py-2.5 pr-4">
                        <Link
                          to={`/admin/avaliacoes/ferramentas?card=${encodeURIComponent(l.slug)}${periodo ? `&${periodo}` : ""}`}
                          className="font-semibold text-link underline-offset-2 hover:underline"
                          data-abrir-ferramenta={l.slug}
                        >
                          {l.nome}
                        </Link>
                        {l.destino !== "interno" && (
                          <span className="ml-2 text-xs text-cinza">{l.destino === "em-breve" ? "em construção" : "abre fora do hub"}</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-4 text-right font-semibold tabular-nums text-titulo" data-media>
                        {notaLegivel(l.media_geral)}
                      </td>
                      {/* `data-meta-atual` declara a meta como o banco a devolveu
                          ("sem" quando não há): o texto da célula é o do botão
                          para quem edita, e a prova não precisa adivinhá-lo. */}
                      <td className="py-2.5 pr-4 text-right tabular-nums text-tinta" data-meta data-meta-atual={l.meta ?? "sem"}>
                        {podeEditar ? (
                          <button
                            type="button"
                            ref={(el) => {
                              if (el) botoes.current.set(l.slug, el);
                              else botoes.current.delete(l.slug);
                            }}
                            onClick={() => alternarEditor(l.slug)}
                            aria-expanded={aberto}
                            aria-controls={aberto ? idDoEditor(l.slug) : undefined}
                            // O nome acessível COMEÇA pelo texto visível (a regra
                            // "rótulo no nome"): quem usa comando de voz diz o que vê.
                            aria-label={l.meta === null ? `Definir meta de ${l.nome}` : `${metaLegivel(l.meta)} — editar a meta de ${l.nome}`}
                            data-editar-meta={l.slug}
                            className={`inline-flex min-h-9 items-center gap-1.5 rounded-lg border px-2.5 py-1 font-semibold tabular-nums transition ${
                              aberto ? "border-acento text-titulo" : "border-borda text-tinta hover:border-acento"
                            }`}
                          >
                            {l.meta === null ? "Definir meta" : metaLegivel(l.meta)}
                            <IconeEditar className="size-3.5 shrink-0 text-cinza" />
                          </button>
                        ) : (
                          metaLegivel(l.meta)
                        )}
                      </td>
                      <td className="py-2.5 pr-4">
                        <SeloDaSituacao linha={l} />
                      </td>
                      <td className="py-2.5 pr-4 text-right tabular-nums text-tinta" data-avaliacoes>
                        {numero(l.avaliacoes)}
                      </td>
                      <td className="py-2.5 pr-4 text-right tabular-nums text-tinta" data-satisfeitas>
                        {pctLegivel(l.satisfeitas_pct)}
                      </td>
                      <td className="py-2.5 pr-4 text-right tabular-nums text-tinta" data-baixas>
                        {l.avaliacoes === 0 ? "—" : numero(l.baixas)}
                      </td>
                      {/* O dia de São Paulo, o mesmo que o período usa: pelo
                          fuso do navegador, uma avaliação das 23h30 de 28/09
                          sairia como 29/09 para quem abre fora do Brasil. */}
                      <td className="py-2.5 text-right tabular-nums text-cinza">{l.ultima ? quandoLegivel(l.ultima).dia : "—"}</td>
                    </tr>
                    {aberto && (
                      <EditorDaMetaNaLista
                        key={l.slug}
                        id={idDoEditor(l.slug)}
                        linha={l}
                        colunas={COLUNAS_DA_LISTA}
                        aoFechar={fecharEditor}
                        aoMudar={aoMudar}
                      />
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <div className="mt-4 max-w-3xl space-y-2 text-xs leading-relaxed text-cinza" data-como-se-le>
        <p>
          <strong className="font-semibold text-tinta">Como se lê.</strong> A média é a média, por avaliação, das três
          notas (utilidade, facilidade e satisfação) — a mesma conta do painel antigo e da página de cada ferramenta. As
          notas vão de 0 a 10: o formulário do hub vai de 1 a 10, e o app antigo aceitava zero.{" "}
          <strong className="font-semibold text-tinta">Satisfeitas</strong> são as avaliações de média 8 ou
          mais; <strong className="font-semibold text-tinta">baixas</strong>, as de média 5 ou menos. A meta é
          comparada com a média como ela aparece aqui, arredondada. Cada número é do período escolhido acima, pelo dia
          de São Paulo.
        </p>
        <p>
          A equipe fica fora das contas. Entram as ferramentas que moram no hub — onde está o botão «Avaliar esta
          ferramenta» — e toda ferramenta que já recebeu avaliação. As avaliações de antes do hub vieram do app antigo (a
          Central Feedbacks) por importação; as do hub chegam na hora.
        </p>
      </div>
    </>
  );
}

/**
 * A linha de edição da meta, logo abaixo da ferramenta — o "Meta 9,0 ✎" do
 * painel antigo, que editava ali mesmo, sem abrir o detalhe.
 *
 * A frase do banco FICA na linha (sucesso ou recusa) até a pessoa fechar ou
 * abrir outra: quem ajusta várias metas em sequência confere cada uma antes de
 * seguir. No sucesso a lista relê — a situação e os números do alto mudam —, e
 * a linha de edição continua aberta, com a frase.
 *
 * O Enter salva (é um formulário), o Esc fecha, o foco vai ao campo ao abrir e
 * volta ao botão da linha ao fechar (quem cuida da volta é a lista, que sabe
 * qual botão abriu).
 */
function EditorDaMetaNaLista({
  id,
  linha,
  colunas,
  aoFechar,
  aoMudar,
}: {
  id: string;
  linha: LinhaDaFerramenta;
  colunas: number;
  aoFechar: () => void;
  aoMudar: () => void;
}) {
  const [texto, setTexto] = useState(linha.meta === null ? "" : metaLegivel(linha.meta));
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [recado, setRecado] = useState<RecadoDaMeta | null>(null);
  // Depois de gravar, "Cancelar" mentiria: não há o que cancelar, só fechar.
  const [gravou, setGravou] = useState(false);
  const campo = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Selecionado, o valor antigo sai com a primeira tecla: o gesto comum é
    // trocar 9,0 por 8,5, e não editar o número por dentro.
    campo.current?.focus();
    campo.current?.select();
  }, []);

  async function salvar(meta: number | null) {
    if (enviando) return;
    setEnviando(true);
    setRecado(null);
    const r = await definirMeta(linha.slug, meta, motivo);
    setEnviando(false);
    setRecado({ tipo: r.ok ? "ok" : "erro", texto: fraseDaResposta(r) });
    if (r.ok) {
      // O campo passa a mostrar o que o BANCO guardou ("8.5" digitado vira "8,5").
      if (r.meta !== undefined) setTexto(r.meta === null ? "" : metaLegivel(r.meta));
      if (r.mudou) {
        setMotivo("");
        setGravou(true);
        aoMudar();
      }
    }
    devolverFocoSePerdido(campo.current);
  }

  function aoEnviar(e: FormEvent) {
    e.preventDefault();
    const n = metaDoTexto(texto);
    if (n === null) {
      setRecado({ tipo: "erro", texto: fraseDoCampoInvalido(linha.meta !== null, texto.trim() === "") });
      return;
    }
    void salvar(n);
  }

  function aoTeclar(e: KeyboardEvent<HTMLFormElement>) {
    if (e.key !== "Escape") return;
    e.preventDefault();
    e.stopPropagation();
    aoFechar();
  }

  return (
    <tr id={id} className="border-b border-borda/60 bg-superficie-2 last:border-0" data-editor-da-meta-na-lista={linha.slug}>
      <td colSpan={colunas} className="px-3 pt-1 pb-4">
        {/* No celular a tabela rola de lado (46rem), e a linha de edição ocupa
            a largura dela inteira: presa à esquerda e do tamanho da faixa
            VISÍVEL, o campo e o «Salvar» ficam à vista sem rolar a tabela. Os
            descontos são os das margens do painel e do cartão (px-4/px-6 do
            painel, p-5/p-6 do cartão, e o fio). A partir de `lg` a tabela cabe,
            e o limite sai. */}
        <div className="sticky left-0 max-w-[calc(100vw-5rem)] sm:max-w-[calc(100vw-6.5rem)] lg:max-w-none">
          <form
            onSubmit={aoEnviar}
            onKeyDown={aoTeclar}
            aria-label={`Meta de avaliação de ${linha.nome}`}
            className="flex flex-wrap items-end gap-3"
          >
            <label className="grid gap-1 text-xs font-semibold text-cinza">
              Meta (de 0 a 10)
              <input
                ref={campo}
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                inputMode="decimal"
                autoComplete="off"
                className={`${CAMPO_BASE} w-28 tabular-nums`}
                data-campo-meta
              />
            </label>
            <label className="grid min-w-[12rem] flex-1 gap-1 text-xs font-semibold text-cinza">
              Por que mudou (opcional)
              <input
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                maxLength={500}
                className={`${CAMPO_BASE} w-full`}
                data-campo-motivo
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <button type="submit" disabled={enviando} className="pill-cta min-h-12 px-5" data-salvar-meta>
                {enviando ? "Salvando…" : "Salvar"}
              </button>
              {linha.meta !== null && (
                <button
                  type="button"
                  disabled={enviando}
                  onClick={() => void salvar(null)}
                  className="min-h-12 rounded-full border border-borda px-5 text-sm font-semibold text-tinta transition hover:border-acento"
                  data-tirar-meta
                >
                  Tirar a meta
                </button>
              )}
              <button
                type="button"
                onClick={aoFechar}
                className="min-h-12 rounded-full px-4 text-sm font-semibold text-cinza transition hover:text-tinta"
                data-cancelar-meta
              >
                {gravou ? "Fechar" : "Cancelar"}
              </button>
            </div>
          </form>
          {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
          <p className="mt-2 text-xs leading-relaxed text-cinza">
            Enter salva e Esc fecha. Cada mudança fica no histórico da ferramenta, na ficha do Catálogo, com quem mudou,
            quando e o motivo.
          </p>
        </div>
      </td>
    </tr>
  );
}

// ── A página de uma ferramenta ──────────────────────────────────────────────

function PaginaDaFerramenta({
  dados,
  slug,
  pedido,
  periodo,
  podeEditar,
  veCatalogo,
  aoMudar,
}: {
  dados: Comparacao;
  slug: string;
  pedido: PedidoDePeriodo;
  periodo: string;
  podeEditar: boolean;
  veCatalogo: boolean;
  aoMudar: () => void;
}) {
  const linha = dados.ferramentas.find((f) => f.slug === slug);
  const voltar = (
    <Link
      to={`/admin/avaliacoes/ferramentas${periodo ? `?${periodo}` : ""}`}
      className="text-sm font-semibold text-link underline-offset-2 hover:underline"
      data-voltar-a-lista
    >
      ← Todas as ferramentas
    </Link>
  );

  if (!linha) {
    return (
      <div className="space-y-4">
        {voltar}
        <div className="surface p-6" data-ferramenta-fora-do-painel>
          <h2 className="font-display text-lg font-bold text-titulo">Esta ferramenta não está no painel</h2>
          <p className="mt-2 text-sm text-cinza">
            O painel lista as ferramentas que moram no hub e as que já receberam avaliação. «{slug}» não é nenhuma das duas
            — ou não é uma ferramenta.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {voltar}
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="font-display text-xl font-bold text-titulo" data-nome-da-ferramenta-do-painel>
          {linha.nome}
        </h2>
        {veCatalogo && (
          <Link
            to={`/admin/catalogo/vitrine?card=${encodeURIComponent(slug)}`}
            className="text-sm font-semibold text-link underline-offset-2 hover:underline"
            data-abrir-ficha-no-catalogo
          >
            Abrir a ficha no Catálogo
          </Link>
        )}
      </div>
      <MetaDaFerramenta linha={linha} podeEditar={podeEditar} aoMudar={aoMudar} />
      <AvaliacoesDaFerramenta linha={linha} pedido={pedido} importacao={dados.importacao} comparacao={dados.periodo.comparacao} />
    </div>
  );
}

function MetaDaFerramenta({
  linha,
  podeEditar,
  aoMudar,
}: {
  linha: LinhaDaFerramenta;
  podeEditar: boolean;
  aoMudar: () => void;
}) {
  const [texto, setTexto] = useState(linha.meta === null ? "" : metaLegivel(linha.meta));
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [recado, setRecado] = useState<RecadoDaMeta | null>(null);
  const campo = useRef<HTMLInputElement>(null);

  // Quando a meta muda no banco (a releitura depois de salvar), o campo acompanha.
  // Só nessa hora: reescrever o campo a cada releitura apagaria o que a pessoa
  // está digitando (a cicatriz da grade, 000317).
  useEffect(() => {
    setTexto(linha.meta === null ? "" : metaLegivel(linha.meta));
  }, [linha.meta]);

  async function salvar(meta: number | null) {
    if (enviando) return;
    setEnviando(true);
    setRecado(null);
    const r = await definirMeta(linha.slug, meta, motivo);
    setEnviando(false);
    // A frase é a do banco, nos dois casos: é ela que diz o que aconteceu.
    setRecado({ tipo: r.ok ? "ok" : "erro", texto: fraseDaResposta(r) });
    if (r.ok && r.mudou) {
      setMotivo("");
      aoMudar();
    }
    devolverFocoSePerdido(campo.current);
  }

  function aoEnviar(e: FormEvent) {
    e.preventDefault();
    const n = metaDoTexto(texto);
    if (n === null) {
      setRecado({ tipo: "erro", texto: fraseDoCampoInvalido(linha.meta !== null, texto.trim() === "") });
      return;
    }
    void salvar(n);
  }

  return (
    <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby="titulo-meta" data-meta-da-ferramenta={linha.slug}>
      <h3 id="titulo-meta" className="font-display text-lg font-bold text-titulo">
        Meta de avaliação
      </h3>
      <dl className="mt-3 flex flex-wrap items-start gap-x-10 gap-y-4">
        <div>
          <dt className="text-xs text-cinza">Meta</dt>
          <dd className="font-display text-3xl font-bold text-titulo" data-meta-atual={linha.meta ?? "sem"}>
            {linha.meta === null ? "Sem meta" : metaLegivel(linha.meta)}
          </dd>
        </div>
        <div>
          {/* O período já está escrito no alto da sub-aba (`LinhaDoPeriodo`, da
              resposta do banco): repeti-lo aqui punha dois travessões num rótulo
              de três palavras. */}
          <dt className="text-xs text-cinza">Média no período</dt>
          <dd className="font-display text-3xl font-bold text-titulo" data-media-da-ferramenta>
            {notaLegivel(linha.media_geral)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-cinza">Situação</dt>
          <dd className="mt-2.5">
            <SeloDaSituacao linha={linha} />
          </dd>
        </div>
      </dl>

      {podeEditar ? (
        <form onSubmit={aoEnviar} className="mt-5 flex flex-wrap items-end gap-3" data-editor-da-meta>
          <label className="grid gap-1 text-xs font-semibold text-cinza">
            Nova meta (de 0 a 10)
            <input
              ref={campo}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              inputMode="decimal"
              autoComplete="off"
              className={`${CAMPO_BASE} w-32 tabular-nums`}
              data-campo-meta
            />
          </label>
          <label className="grid min-w-[14rem] flex-1 gap-1 text-xs font-semibold text-cinza">
            Por que mudou (opcional)
            <input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              maxLength={500}
              className={`${CAMPO_BASE} w-full`}
              data-campo-motivo
            />
          </label>
          <button type="submit" disabled={enviando} className="pill-cta min-h-12 px-5" data-salvar-meta>
            {enviando ? "Salvando…" : "Salvar a meta"}
          </button>
          {linha.meta !== null && (
            <button
              type="button"
              disabled={enviando}
              onClick={() => void salvar(null)}
              className="min-h-12 rounded-full border border-borda px-5 text-sm font-semibold text-tinta transition hover:border-acento"
              data-tirar-meta
            >
              Tirar a meta
            </button>
          )}
        </form>
      ) : (
        <p className="mt-4 text-sm text-cinza" data-meta-so-leitura>
          Quem muda a meta é quem tem edição na aba Avaliações — quem dá é um master, em Admin › Permissões.
        </p>
      )}
      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
      <p className="mt-3 text-xs leading-relaxed text-cinza">
        Cada mudança fica no histórico da ferramenta, na ficha do Catálogo, com quem mudou, quando e o motivo.
      </p>
    </section>
  );
}
